import Promise from "bluebird";
import ko from "knockout";
import _ from "lodash";
import apiCore from "services/apiCore";
import authService from "services/authService";
import logger from "services/loggingService";

export enum FeatureToggle {
    QMHAWKTOPICSV2 = "qm.hawk.topics.v2",
    TARTARUS_AS_CONVERSATION_SOURCE = "qm.release.tartarus.useAsConversationSource",
    EVALUATION_ASSIGNEE = "qm.release.evaluation.assignee",
    EVALUATION_DISPUTES = "qm.release.evaluation.disputes",
    EVALUATION_EVALUATOR_CHANGE_FEATURE_TOGGLE_NAME = "qm.release.evaluation.evaluatorchange",
    STA_SPLIT_TRANSCRIPT_VIEW_PERMISSION = "staSplitTranscriptViewPermission",
    STA_CATEGORY = "staCategories",
    EMAIL_PARKING = "pure4201EmailParking",
    MESSAGE_PARKING = "PURE-4524_message_parking",
    VOICE_SURVEY = "qm.release.survey.voiceIVRSurvey",
    TEST_TOGGLE = "test.toggle.do.not.delete",
    WEM_964_AI_SCORING = "qm.wem-964.evaluation.ai.scoring",
    PURE_5797_MESSAGING_TWITTER_PRIVATE_DIRECT_MESSAGES = "PURE-5797_MESSAGING_TWITTER_PRIVATE_DIRECT_MESSAGES",
    PURE_5806_DIGITAL_CONSULT = "PURE-5806.digital.consult",
    WEM_1128_IDUI_SPARK_V4 = "WEM-1128_iduiSparkV4",
    PURE_5422_VIDEO_CHAT = "pure-5422-video-chat",
    WEM_1308_REMOVE_ARCHIVAL_RESTORATIONS_LIMIT = "WEM-1308_Remove_Archival_Restorations_Limit",
    WEM_1249_LOAD_TRANSCRIPT_WITHOUT_RECORDING = "WEM-1249_loadTranscriptWithoutRecording",
    WEM_1378_ADD_SCORE_TO_QUALITY_SUMMARY = "WEM-1378_ADD_SCORE_TO_QUALITY_SUMMARY",
    PURE_2946_INTERNATIONALIZATION_DATE_TIME = "PURE-2946_InternationalizationOfDatesAndTimes",
    REC_7506_CHECK_FOR_OGG_SUPPORT_FT = "REC-7506_checkForOggSupport",
    AUTOMATED_EVALUATION_COMPLETION = "QM-18908_Automated_Evaluation_Completion",
    EVALUATION_FORM_DEFAULT_ANSWERING = "WEM-1359_EVALUATION_FORM_DEFAULT_ANSWERING",
    WEM_1418_IDUI_SPARK_V4_FLARE = "WEM-1418_iduiSparkV4_Flare",
    WEM_437_SCREEN_MONITORING = "WEM-437_screen-monitoring",
    WEM_118_SNIPPET_RECORDING = "WEM-118_snippet-recording",
    PURE_5534_OPEN_MESSAGING_ENGAGEMENT_SOURCE_TAGGING = "PURE-5534_OpenMessagingEngagementSourceTagging",
    PURE_5920_DISABLE_LEGACY_AUTH = "PURE-5920_disable_legacy_auth",
    REC_9720_TIMELINE_IMPROVEMENTS = "REC-9720_timeline-improvements",
    EMAIL_TRANSCRIPT_UI_V2 = "GIA-27948_Transcript_Ui_V2",
    QM_19313_EVALUATION_DEDUPLICATION = "QM-19313_Evaluation_Deduplication",
    PURE_7349_ACD_CONFERENCE_MS3_PHASE_1 = "PURE-7349_acdConferenceMS3-phase-1",
    REC_5171_USE_NEW_ANALYTICS_ROUTE = "REC-5171_useNewAnalyticsRoute",
    REC_6867_OPTIMIZE_API_CALLS_IN_IDUI = "REC-6867_optimizeAPICallsInIDUI",
}

type FeatureToggleDictionary<T> = Record<FeatureToggle, T>;
export type ObservablePromise<T> = ko.Observable<T> &
    Promise<T> & {
        resolve: (thenableOrResult: unknown) => void;
        reject: (error?: Error) => void;
    };

/**
 * A service abstraction around obtaining the feature flags for the current user session.
 */

class FeatureToggleService {
    private featureToggleCache: FeatureToggleDictionary<
        ObservablePromise<boolean>
    > = {} as FeatureToggleDictionary<ObservablePromise<boolean>>;
    constructor() {
        Object.values(FeatureToggle).forEach((featureToggle) => {
            this.featureToggleCache[featureToggle] =
                this.buildFeatureToggleObservable(featureToggle);
        });
    }

    buildFeatureToggleObservable(
        feature: FeatureToggle
    ): ObservablePromise<boolean> {
        const observable = ko.observable(false) as ObservablePromise<boolean>;
        const promise = new Promise(function (resolve, reject) {
            observable.resolve = resolve;
            observable.reject = reject;
        });
        observable.then = _.bind(promise.then, promise);
        observable.catch = _.bind(promise.catch, promise);
        observable.finally = _.bind(promise.finally, promise);

        this.featureToggleCache[feature] = observable;

        return observable;
    }

    initializeFeatureToggleValue(
        observable: ObservablePromise<boolean>,
        featureKey: FeatureToggle
    ) {
        const loadComputed = ko.computed(function () {
            if (authService.isLoggedIn()) {
                (apiCore as any).v2
                    .get("/featureToggles", { feature: featureKey })
                    .then(function (
                        features: FeatureToggleDictionary<boolean>
                    ) {
                        const toggleValue = !!features[featureKey];
                        observable(toggleValue);
                        observable.resolve(toggleValue);
                        loadComputed.dispose();
                    })
                    .catch(function (error: Error) {
                        logger.error("Unable to load feature toggle", error);
                        observable.reject(error);
                    });
            }
        });
    }

    getFeatureToggle(featureKey: FeatureToggle) {
        if (!this.featureToggleCache[featureKey]) {
            const observable = this.buildFeatureToggleObservable(featureKey);
            this.initializeFeatureToggleValue(observable, featureKey);
        }
        return this.featureToggleCache[featureKey];
    }

    isEnabled(featureKey: FeatureToggle) {
        return this.getFeatureToggle(featureKey)();
    }

    init() {
        return this.getFeatureToggles(Object.values(FeatureToggle)).then(
            (features: FeatureToggleDictionary<boolean>) =>
                _.forEach(features, (value, featureKey) => {
                    const observable = this.getFeatureToggle(
                        featureKey as FeatureToggle
                    );
                    observable(value);
                    observable.resolve(value);
                })
        );
    }

    /**
     * Get an object of feature toggle key:value pairs
     *
     * @param  {array} featureKeys=[] feature toggles to check
     * @returns {Promise} resolves to object of shape { 'feature.toggle.name': true|false }
     */
    getFeatureToggles(
        featureKeys: string[]
    ): Promise<FeatureToggleDictionary<boolean>> {
        if (authService.isLoggedIn()) {
            return (apiCore as any).v2
                .get(`/featureToggles?feature=${featureKeys.toString()}`)
                .then((features: FeatureToggleDictionary<boolean>) => {
                    return features;
                })
                .catch((error: Error) => {
                    logger.error("Unable to load feature toggle", error);
                });
        }
        return Promise.reject("Not logged in");
    }

    /**
     * Get an array of feature toggles which are enabled.
     * Passed in features which are not enabled will be omitted from the return array.
     *
     * @param {array} featureKeys=[] feature toggles to check
     * @returns {array} an array of enabled feature toggles
     */
    getFeatureTogglesArray(featureKeys = []) {
        return this.getFeatureToggles(featureKeys).then((toggleObject) => {
            const enabledToggleList: FeatureToggle[] = [];
            Object.keys(toggleObject).forEach((key) => {
                if (toggleObject[key as FeatureToggle] !== false) {
                    enabledToggleList.push(key as FeatureToggle);
                }
            });
            return enabledToggleList;
        });
    }
}

export default new FeatureToggleService();
